import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getIncubation } from '../../controllers/incubations';
import { createParticipant } from '../../controllers/participants';
import { getOrganizations } from '../../controllers/organizations';
import { getUsers } from '../../controllers/users';
import { keyToDisplayString, displayStringToKey } from '../../utils/StringUtils';
import { get_cat_breeds, get_dog_breeds } from '../../utils/BreedUtils';
import { get_us_states, get_timezone_for_state, get_timezone_display } from '../../utils/StateUtils';
import Card from '../../components/ui/Card/Card';
import Input from '../../components/ui/Input/Input';
import Select from '../../components/ui/Select/Select';
import Button from '../../components/ui/Button/Button';
import Switch from '../../components/ui/Switch/Switch';
import Spinner from '../../components/ui/Spinner/Spinner';
import { useToast } from '../../contexts/ToastContext';
import './AddParticipantsScreen.css';

const AddParticipantsScreen = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { showError, showSuccess, showWarning } = useToast();
    const [incubation, setIncubation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [form_data, setFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [organizations, setOrganizations] = useState([]);
    const [organizationsLoading, setOrganizationsLoading] = useState(true);
    const [dashboardUsers, setDashboardUsers] = useState([]);
    const [dashboardUsersLoading, setDashboardUsersLoading] = useState(true);
    const [showSurveyModal, setShowSurveyModal] = useState(false);
    const [selectedSurveys, setSelectedSurveys] = useState([]);

    // Get incubation ID from URL params
    const incubation_id = location.state?.incubationId;

    useEffect(() => {
        if (incubation_id) {
            fetch_incubation_data();
        } else {
            console.error('No incubation ID provided');
            navigate('/home');
        }
    }, [incubation_id]);

    useEffect(() => {
        const fetchOrganizations = async () => {
            try {
                const response = await getOrganizations({ page: 1, limit: 1000 });
                if (response.data && response.data.organizations) {
                    setOrganizations(response.data.organizations);
                }
            } catch (error) {
                console.error('Failed to fetch organizations:', error);
                showError('Failed to load organizations. Please try again.');
            } finally {
                setOrganizationsLoading(false);
            }
        };

        fetchOrganizations();
    }, [showError]);

    useEffect(() => {
        const fetchDashboardUsers = async () => {
            try {
                const response = await getUsers({ page: 1, limit: 1000 });
                if (response.data && response.data.success) {
                    setDashboardUsers(response.data.users || []);
                }
            } catch (error) {
                console.error('Failed to fetch dashboard users:', error);
                showError('Failed to load dashboard users. Please try again.');
            } finally {
                setDashboardUsersLoading(false);
            }
        };

        fetchDashboardUsers();
    }, [showError]);

    // ==== DATA FETCHING ========================================================================================

    const fetch_incubation_data = async () => {
        try {
            setLoading(true);
            const response = await getIncubation({ incubation_id: incubation_id });
            
            if (response.data && response.data.success) {
                const incubation_data = response.data.incubation;
                setIncubation(incubation_data);
                
                // Initialize form data based on data_fields and inclusion_criteria
                const initial_form_data = {};
                
                // Add data_fields (excluding auto-generated ones) - use display_name if available
                if (incubation_data.data_fields && Array.isArray(incubation_data.data_fields)) {
                    incubation_data.data_fields.forEach(field => {
                        if (field.type !== 'Auto Generated') {
                            const display_name = field.display_name || keyToDisplayString(field.name);
                            initial_form_data[`data_${display_name}`] = '';
                        }
                    });
                }
                
                setFormData(initial_form_data);
            } else {
                console.error('Failed to fetch incubation data:', response.data?.error);
                showError('Failed to load incubation data');
                navigate('/home');
            }
        } catch (error) {
            console.error('Error fetching incubation data:', error);
            showError('Error loading incubation data');
            navigate('/home');
        } finally {
            setLoading(false);
        }
    };

    // ================================================================================================================

    // ==== UTILITY FUNCTIONS ========================================================================================

    const format_date_input = (value) => {
        // Remove all non-numeric characters
        const numbers_only = value.replace(/\D/g, '');
        
        // Limit to 8 digits (MMDDYYYY)
        const limited = numbers_only.slice(0, 8);
        
        // Add slashes at appropriate positions
        if (limited.length <= 2) {
            return limited;
        } else if (limited.length <= 4) {
            return `${limited.slice(0, 2)}/${limited.slice(2)}`;
        } else {
            return `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4)}`;
        }
    };

    const handle_input_change = (field_name, value) => {
        // Check if this is a date field - need to compare with original field names from backend
        const is_date_field = incubation?.data_fields?.some(field => {
            const display_name = field.display_name || keyToDisplayString(field.name);
            return display_name === field_name.replace('data_', '') && field.type === 'date';
        });
        
        let formatted_value = value;
        if (is_date_field) {
            formatted_value = format_date_input(value);
        }
        
        setFormData(prev => ({
            ...prev,
            [field_name]: formatted_value
        }));
        
        // Clear error when user starts typing
        if (errors[field_name]) {
            setErrors(prev => ({
                ...prev,
                [field_name]: ''
            }));
        }
    };

    const validate_date = (date_string) => {
        // Check if the date matches MM/DD/YYYY format
        const date_regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
        return date_regex.test(date_string);
    };

    // ================================================================================================================

    // ==== FORM HANDLING ========================================================================================

    const validate_form = () => {
        const new_errors = {};
        
        // Validate data_fields (excluding auto-generated ones) - work with display names
        if (incubation?.data_fields && Array.isArray(incubation.data_fields)) {
            incubation.data_fields.forEach(field => {
                if (field.type !== 'Auto Generated') {
                    const display_name = field.display_name || keyToDisplayString(field.name);
                    const field_key = `data_${display_name}`;
                    const field_value = form_data[field_key];
                    
                    // Check if field is required and empty
                    if (field.required && (!field_value || field_value.trim() === '')) {
                        new_errors[field_key] = `${display_name} is required`;
                    }
                    
                    // Check date format validation for date fields
                    if (field.type === 'date' && field_value && field_value.trim() !== '') {
                        if (!validate_date(field_value)) {
                            new_errors[field_key] = `${display_name} must be in MM/DD/YYYY format`;
                        }
                    }
                }
            });
        }
        
        // Note: inclusion_criteria are not validated - they default to false if not toggled
        
        setErrors(new_errors);
        return Object.keys(new_errors).length === 0;
    };

    const handle_submit = async (e) => {
        e.preventDefault();
        await handle_submit_internal(null);
    };

    const handle_submit_internal = async (surveyLinks = null) => {
        if (!validate_form()) {
            showWarning('Please fill in all required fields');
            return;
        }

        try {
            setSubmitting(true);

            // Separate data_fields - convert display names back to keys
            const data_fields = {};

            Object.keys(form_data).forEach(key => {
                if (key.startsWith('data_')) {
                    const display_name = key.replace('data_', '');
                    const field_key = displayStringToKey(display_name); // Convert display name back to key
                    data_fields[field_key] = form_data[key];
                }
            });

            const participant_data = {
                incubation_id: incubation_id,
                data_fields: data_fields,
                survey_links: surveyLinks
            };

            console.log('Creating participant with data:', participant_data);

            const response = await createParticipant(participant_data);

            if (response.data && response.data.success) {
                const successMessage = surveyLinks && surveyLinks.length > 0
                    ? `Participant added successfully! Welcome email with ${surveyLinks.length} survey${surveyLinks.length > 1 ? 's' : ''} sent.`
                    : 'Participant added successfully!';
                showSuccess(successMessage);
                navigate('/incubation', { state: { incubationId: incubation_id, refreshParticipants: true } });
            } else {
                console.error('Failed to create participant:', response.data?.error);
                showError(`Failed to create participant: ${response.data?.error || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error creating participant:', error);
            showError('Error creating participant. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handle_back_click = () => {
        if (incubation_id) {
            navigate('/incubation', { state: { incubationId: incubation_id } });
        } else {
            navigate('/home');
        }
    };

    const handleSurveyToggle = (survey) => {
        setSelectedSurveys(prev => {
            const exists = prev.find(s => s.url === survey.url);
            if (exists) {
                return prev.filter(s => s.url !== survey.url);
            } else {
                return [...prev, survey];
            }
        });
    };

    const handleSubmitWithSurveys = async (e) => {
        e.preventDefault();
        if (selectedSurveys.length === 0) {
            showError('Please select at least one survey');
            return;
        }
        setShowSurveyModal(false);
        await handle_submit_internal(selectedSurveys);
    };

    const normalizeOrgName = (name) => (name || '').trim().toLowerCase();
    const sourceFieldDefinition = incubation?.data_fields?.find(field => field.name === 'source');
    const sourceFieldDisplayName = sourceFieldDefinition
        ? (sourceFieldDefinition.display_name || keyToDisplayString(sourceFieldDefinition.name))
        : '';
    const sourceFieldFormKey = sourceFieldDisplayName ? `data_${sourceFieldDisplayName}` : null;
    const selectedSourceOrganization = sourceFieldFormKey ? (form_data[sourceFieldFormKey] || '') : '';
    const selectedSourceOrgNormalized = selectedSourceOrganization ? normalizeOrgName(selectedSourceOrganization) : '';
    const dashboardUserOptionsWithMeta = dashboardUsers.map((user, optionIndex) => {
        const fullName = `${(user.first_name || '').trim()} ${(user.last_name || '').trim()}`.trim();
        const label = fullName || user.email || `User #${optionIndex + 1}`;
        const organizationName = user.organization?.name || user.organization || '';
        return {
            value: label,
            label,
            key: user.id || `${label}-${optionIndex}`,
            organizationName,
            organizationNormalized: normalizeOrgName(organizationName)
        };
    });
    const filteredDashboardUserOptions = selectedSourceOrgNormalized
        ? dashboardUserOptionsWithMeta.filter(option => option.organizationNormalized === selectedSourceOrgNormalized)
        : dashboardUserOptionsWithMeta;
    const dashboardContactPlaceholder = dashboardUsersLoading
        ? 'Loading dashboard users...'
        : filteredDashboardUserOptions.length > 0
            ? (selectedSourceOrganization ? `Select contact from ${selectedSourceOrganization}` : 'Select contact')
            : (selectedSourceOrganization ? `No dashboard users found for ${selectedSourceOrganization}` : 'No dashboard users available');

    // ================================================================================================================

    // ==== RENDER FUNCTIONS ========================================================================================

    const render_loading_screen = () => {
        return (
            <div className="add-participants-screen">
                <div className="add-participants-header-container">
                    <button className="add-participants-back-button" onClick={handle_back_click}>
                        <i className="fas fa-arrow-left"></i>
                        <span>Back</span>
                    </button>
                </div>
                <div className="add-participants-content">
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spinner full size="lg" label="Loading incubation data..." />
                    </div>
                </div>
            </div>
        );
    };

    const render_error_screen = () => {
        return (
            <div className="add-participants-screen">
                <div className="add-participants-header-container">
                    <button className="add-participants-back-button" onClick={handle_back_click}>
                        <i className="fas fa-arrow-left"></i>
                        <span>Back</span>
                    </button>
                </div>
                <div className="add-participants-content">
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <p>Incubation not found</p>
                        <Button variant="primary" onClick={() => navigate('/home')}>
                            Go to Dashboard
                        </Button>
                    </div>
                </div>
            </div>
        );
    };

    // ================================================================================================================

    if (loading) {
        return render_loading_screen();
    }

    if (!incubation) {
        return render_error_screen();
    }

    return (
        <div className="add-participants-screen">
            <div className="add-participants-header-container">
                <button className="add-participants-back-button" onClick={handle_back_click}>
                    <i className="fas fa-arrow-left"></i>
                    <span>Back</span>
                </button>
            </div>
            
            <div className="add-participants-content">
                <Card className="add-participants-form-card">
                    <form onSubmit={handle_submit} className="add-participants-form">
                        <div className="form-header">
                            <h2>Participant Information</h2>
                            <p>Please fill in the following information for the new participant:</p>
                        </div>
                        
                        <div className="form-fields">
                            {/* Auto Generated Fields Section */}
                            {incubation.data_fields && incubation.data_fields.some(field => field.type === 'Auto Generated') && (
                                <div className="form-section">
                                    <h3 className="form-section-title">Auto Generated Fields</h3>
                                    <div className="form-section-fields">
                                        {incubation.data_fields
                                            .filter(field => field.type === 'Auto Generated')
                                            .map((field, index) => (
                                                <div key={index} className="form-field auto-generated-field">
                                                    <label htmlFor={`auto_${field.name}`} className="form-label">
                                                        {field.name}
                                                        <span className="auto-generated-badge">Auto Generated</span>
                                                    </label>
                                                    <Input
                                                        id={`auto_${field.name}`}
                                                        type="text"
                                                        value="Will be generated automatically"
                                                        disabled
                                                        className="auto-generated-input"
                                                    />
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            )}

                            {/* Data Fields Section */}
                            {incubation.data_fields && incubation.data_fields.some(field => field.type !== 'Auto Generated') && (
                                <div className="form-section">
                                    <h3 className="form-section-title">Data Fields</h3>
                                    <div className="form-section-fields">
                                        {incubation.data_fields
                                            .filter(field => field.type !== 'Auto Generated')
                                            .map((field, index) => {
                                                const display_name = field.display_name || keyToDisplayString(field.name);
                                                const field_key = `data_${display_name}`;
                                                
                                                // Render Select for choice fields
                                                if (field.type === 'choices' && field.choices && field.choices.length > 0) {
                                                    const options = field.choices.map(choice => ({
                                                        value: choice,
                                                        label: choice
                                                    }));
                                                    
                                                    return (
                                                        <div key={index} className="form-field">
                                                            <label htmlFor={field_key} className="form-label">
                                                                {display_name}
                                                                {field.required && <span className="required-asterisk">*</span>}
                                                            </label>
                                                            <Select
                                                                id={field_key}
                                                                options={options}
                                                                value={form_data[field_key] || ''}
                                                                onChange={(e) => handle_input_change(field_key, e.target.value)}
                                                                placeholder={`Select ${display_name.toLowerCase()}`}
                                                                className={errors[field_key] ? 'error' : ''}
                                                            />
                                                            {errors[field_key] && (
                                                                <span className="error-message">{errors[field_key]}</span>
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                
                                                // Render Select for Yes/No fields
                                                if (field.type === 'yes_no') {
                                                    const options = [
                                                        { value: 'Yes', label: 'Yes' },
                                                        { value: 'No', label: 'No' }
                                                    ];
                                                    
                                                    return (
                                                        <div key={index} className="form-field">
                                                            <label htmlFor={field_key} className="form-label">
                                                                {display_name}
                                                                {field.required && <span className="required-asterisk">*</span>}
                                                            </label>
                                                            <Select
                                                                id={field_key}
                                                                options={options}
                                                                value={form_data[field_key] || ''}
                                                                onChange={(e) => handle_input_change(field_key, e.target.value)}
                                                                placeholder={`Select ${display_name.toLowerCase()}`}
                                                                className={errors[field_key] ? 'error' : ''}
                                                            />
                                                            {errors[field_key] && (
                                                                <span className="error-message">{errors[field_key]}</span>
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                
                                                // Render organization dropdown for source field
                                                if (field.name === 'source') {
                                                    const organizationOptions = organizations.map((org, optionIndex) => ({
                                                        value: org.name,
                                                        label: org.name,
                                                        key: org.organization_id || org.id || `${org.name}-${optionIndex}`
                                                    }));
                                                    const sourcePlaceholder = organizationsLoading
                                                        ? 'Loading organizations...'
                                                        : organizationOptions.length > 0
                                                            ? 'Select organization'
                                                            : 'No organizations available';
                                                    
                                                    return (
                                                        <div key={index} className="form-field">
                                                            <label htmlFor={field_key} className="form-label">
                                                                {display_name}
                                                                {field.required && <span className="required-asterisk">*</span>}
                                                            </label>
                                                            <Select
                                                                id={field_key}
                                                                options={organizationOptions}
                                                                value={form_data[field_key] || ''}
                                                                onChange={(e) => handle_input_change(field_key, e.target.value)}
                                                                placeholder={sourcePlaceholder}
                                                                className={errors[field_key] ? 'error' : ''}
                                                                disabled={organizationsLoading}
                                                            />
                                                            {errors[field_key] && (
                                                                <span className="error-message">{errors[field_key]}</span>
                                                            )}
                                                        </div>
                                                    );
                                                }
                                            
                                            if (field.name === 'source_contact') {
                                                return (
                                                    <div key={index} className="form-field">
                                                        <label htmlFor={field_key} className="form-label">
                                                            {display_name}
                                                            {field.required && <span className="required-asterisk">*</span>}
                                                        </label>
                                                        <Select
                                                            id={field_key}
                                                            options={filteredDashboardUserOptions}
                                                            value={form_data[field_key] || ''}
                                                            onChange={(e) => handle_input_change(field_key, e.target.value)}
                                                            placeholder={dashboardContactPlaceholder}
                                                            className={errors[field_key] ? 'error' : ''}
                                                            disabled={dashboardUsersLoading}
                                                        />
                                                        {errors[field_key] && (
                                                            <span className="error-message">{errors[field_key]}</span>
                                                        )}
                                                    </div>
                                                );
                                            }

                                                // Check if this is the Owner State field
                                                const isOwnerStateField = display_name === 'Owner State' ||
                                                                    field.name === displayStringToKey('Owner State');

                                                if (isOwnerStateField) {
                                                    const usStates = get_us_states();
                                                    // Find the timezone field key
                                                    const timezoneField = incubation.data_fields?.find(f =>
                                                        f.display_name === 'Owner Timezone' ||
                                                        f.name === displayStringToKey('Owner Timezone')
                                                    );
                                                    const timezoneFieldKey = timezoneField ? `data_${timezoneField.display_name || keyToDisplayString(timezoneField.name)}` : null;

                                                    const handleStateChange = (stateValue) => {
                                                        handle_input_change(field_key, stateValue);
                                                        // Auto-update timezone when state changes
                                                        if (timezoneFieldKey) {
                                                            const timezone = get_timezone_for_state(stateValue);
                                                            const timezoneDisplay = get_timezone_display(timezone);
                                                            handle_input_change(timezoneFieldKey, timezoneDisplay);
                                                        }
                                                    };

                                                    return (
                                                        <div key={index} className="form-field">
                                                            <label htmlFor={field_key} className="form-label">
                                                                {display_name}
                                                                {field.required && <span className="required-asterisk">*</span>}
                                                            </label>
                                                            <Select
                                                                id={field_key}
                                                                options={usStates}
                                                                value={form_data[field_key] || ''}
                                                                onChange={(e) => handleStateChange(e.target.value)}
                                                                placeholder="Select state"
                                                                className={errors[field_key] ? 'error' : ''}
                                                            />
                                                            {errors[field_key] && (
                                                                <span className="error-message">{errors[field_key]}</span>
                                                            )}
                                                        </div>
                                                    );
                                                }

                                                // Check if this is the Owner Timezone field (read-only, auto-populated from state)
                                                const isOwnerTimezoneField = display_name === 'Owner Timezone' ||
                                                                    field.name === displayStringToKey('Owner Timezone');

                                                if (isOwnerTimezoneField) {
                                                    return (
                                                        <div key={index} className="form-field">
                                                            <label htmlFor={field_key} className="form-label">
                                                                {display_name}
                                                                {field.required && <span className="required-asterisk">*</span>}
                                                            </label>
                                                            <Input
                                                                id={field_key}
                                                                type="text"
                                                                value={form_data[field_key] || ''}
                                                                readOnly
                                                                disabled
                                                                placeholder="Auto-filled from state"
                                                                className={errors[field_key] ? 'error' : ''}
                                                                style={{ backgroundColor: '#f3f4f6', color: '#6b7280' }}
                                                            />
                                                            {errors[field_key] && (
                                                                <span className="error-message">{errors[field_key]}</span>
                                                            )}
                                                        </div>
                                                    );
                                                }

                                                // Check if this is a phone number field
                                                const isPhoneField = display_name === 'Owner Phone Number' ||
                                                                    display_name === 'Owner Phone Number - Alternate' ||
                                                                    field.name === displayStringToKey('Owner Phone Number') ||
                                                                    field.name === displayStringToKey('Owner Phone Number - Alternate');

                                                if (isPhoneField) {
                                                    // Format phone number for display: (555) 555-5555
                                                    const formatPhoneForDisplay = (digits) => {
                                                        if (!digits) return '';
                                                        // Ensure we only have digits
                                                        const cleanDigits = digits.replace(/\D/g, '').slice(0, 10);

                                                        if (cleanDigits.length === 0) return '';
                                                        if (cleanDigits.length <= 3) return `(${cleanDigits}`;
                                                        if (cleanDigits.length <= 6) return `(${cleanDigits.slice(0, 3)}) ${cleanDigits.slice(3)}`;
                                                        return `(${cleanDigits.slice(0, 3)}) ${cleanDigits.slice(3, 6)}-${cleanDigits.slice(6)}`;
                                                    };

                                                    const handlePhoneChange = (value) => {
                                                        // Store only digits in form_data
                                                        const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
                                                        handle_input_change(field_key, digitsOnly);
                                                    };

                                                    // Get stored digits and format for display
                                                    const storedDigits = form_data[field_key] || '';
                                                    const displayValue = formatPhoneForDisplay(storedDigits);

                                                    return (
                                                        <div key={index} className="form-field">
                                                            <label htmlFor={field_key} className="form-label">
                                                                {display_name}
                                                                {field.required && <span className="required-asterisk">*</span>}
                                                            </label>
                                                            <Input
                                                                id={field_key}
                                                                type="tel"
                                                                value={displayValue}
                                                                onChange={(e) => handlePhoneChange(e.target.value)}
                                                                placeholder="(555) 555-5555"
                                                                className={errors[field_key] ? 'error' : ''}
                                                                maxLength={14}
                                                            />
                                                            {errors[field_key] && (
                                                                <span className="error-message">{errors[field_key]}</span>
                                                            )}
                                                        </div>
                                                    );
                                                }

                                                // Check if this is a date picker field (consent date or pet birthdate)
                                                const isDatePickerField = display_name === 'Owner consent form - date fully signed' ||
                                                                    display_name === 'Pet Birthdate - Reported' ||
                                                                    field.name === displayStringToKey('Owner consent form - date fully signed') ||
                                                                    field.name === displayStringToKey('Pet Birthdate - Reported');

                                                if (isDatePickerField) {
                                                    // Calculate date constraints
                                                    const today = new Date();
                                                    const maxDate = today.toISOString().split('T')[0]; // Today (no future dates)
                                                    const minDate = new Date(today.getFullYear() - 30, today.getMonth(), today.getDate()).toISOString().split('T')[0]; // 30 years ago

                                                    // Check if this is specifically the Pet Birthdate field
                                                    const isPetBirthdateField = display_name === 'Pet Birthdate - Reported' ||
                                                                                field.name === displayStringToKey('Pet Birthdate - Reported');

                                                    // Convert MM/DD/YYYY to YYYY-MM-DD for date input
                                                    const toInputFormat = (mmddyyyy) => {
                                                        if (!mmddyyyy) return '';
                                                        const parts = mmddyyyy.split('/');
                                                        if (parts.length !== 3) return '';
                                                        const [mm, dd, yyyy] = parts;
                                                        return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
                                                    };

                                                    // Convert YYYY-MM-DD to MM/DD/YYYY for storage
                                                    const toStorageFormat = (yyyymmdd) => {
                                                        if (!yyyymmdd) return '';
                                                        const parts = yyyymmdd.split('-');
                                                        if (parts.length !== 3) return '';
                                                        const [yyyy, mm, dd] = parts;
                                                        return `${mm}/${dd}/${yyyy}`;
                                                    };

                                                    // Calculate age from birthdate and check if over 30 years
                                                    const calculateAge = (mmddyyyy) => {
                                                        if (!mmddyyyy) return null;
                                                        const parts = mmddyyyy.split('/');
                                                        if (parts.length !== 3) return null;
                                                        const [mm, dd, yyyy] = parts;
                                                        const birthDate = new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd));
                                                        const ageDiff = today - birthDate;
                                                        const ageDate = new Date(ageDiff);
                                                        return Math.abs(ageDate.getUTCFullYear() - 1970);
                                                    };

                                                    const handleDateChange = (value) => {
                                                        const storageValue = toStorageFormat(value);
                                                        handle_input_change(field_key, storageValue);
                                                    };

                                                    const storedValue = form_data[field_key] || '';
                                                    const inputValue = toInputFormat(storedValue);

                                                    // Check for age validation error (only for Pet Birthdate)
                                                    const petAge = isPetBirthdateField ? calculateAge(storedValue) : null;
                                                    const ageValidationError = isPetBirthdateField && petAge !== null && petAge > 30
                                                        ? 'Birthday is invalid, pet cannot be older than 30 years.'
                                                        : null;

                                                    // Open date picker when clicking anywhere on the input
                                                    const handleDateClick = (e) => {
                                                        try {
                                                            e.target.showPicker();
                                                        } catch (err) {
                                                            // showPicker() may not be supported in all browsers
                                                        }
                                                    };

                                                    return (
                                                        <div key={index} className="form-field">
                                                            <label htmlFor={field_key} className="form-label">
                                                                {display_name}
                                                                {field.required && <span className="required-asterisk">*</span>}
                                                            </label>
                                                            <Input
                                                                id={field_key}
                                                                type="date"
                                                                value={inputValue}
                                                                onChange={(e) => handleDateChange(e.target.value)}
                                                                onClick={handleDateClick}
                                                                min={minDate}
                                                                max={maxDate}
                                                                className={(errors[field_key] || ageValidationError) ? 'error' : ''}
                                                                style={{ cursor: 'pointer' }}
                                                            />
                                                            {ageValidationError && (
                                                                <span className="error-message">{ageValidationError}</span>
                                                            )}
                                                            {errors[field_key] && !ageValidationError && (
                                                                <span className="error-message">{errors[field_key]}</span>
                                                            )}
                                                        </div>
                                                    );
                                                }

                                                // Check if this is a breed field (primary or secondary) and pet_type is 'cat' or 'dog'
                                                const isPrimaryBreedField = display_name === 'Pet Breed Reported - Primary' ||
                                                                    field.name === displayStringToKey('Pet Breed Reported - Primary') ||
                                                                    display_name === 'Pet Primary Breed - Reported' ||
                                                                    field.name === displayStringToKey('Pet Primary Breed - Reported');
                                                const isSecondaryBreedField = display_name === 'Pet Breed Reported - Secondary' ||
                                                                    field.name === displayStringToKey('Pet Breed Reported - Secondary');
                                                const isBreedField = isPrimaryBreedField || isSecondaryBreedField;
                                                const petTypeFieldKey = incubation.data_fields?.find(f =>
                                                    f.name === 'pet_type' ||
                                                    f.display_name === 'Pet Type' ||
                                                    (f.display_name || keyToDisplayString(f.name)) === 'Pet Type'
                                                );
                                                const petTypeDisplayName = petTypeFieldKey?.display_name || keyToDisplayString(petTypeFieldKey?.name || 'pet_type');
                                                const petTypeValue = form_data[`data_${petTypeDisplayName}`] || '';
                                                const petTypeLower = petTypeValue ? petTypeValue.toLowerCase() : '';
                                                const isCat = petTypeLower === 'cat';
                                                const isDog = petTypeLower === 'dog';

                                                if (isBreedField && isCat) {
                                                    const catBreeds = get_cat_breeds();
                                                    // For secondary breed, add 'None' at the beginning
                                                    const breedList = isSecondaryBreedField ? ['None', ...catBreeds] : catBreeds;
                                                    const catBreedOptions = breedList.map(breed => ({
                                                        value: breed,
                                                        label: breed
                                                    }));

                                                    return (
                                                        <div key={index} className="form-field">
                                                            <label htmlFor={field_key} className="form-label">
                                                                {display_name}
                                                                {field.required && <span className="required-asterisk">*</span>}
                                                            </label>
                                                            <Select
                                                                id={field_key}
                                                                options={catBreedOptions}
                                                                value={form_data[field_key] || (isSecondaryBreedField ? 'None' : '')}
                                                                onChange={(e) => handle_input_change(field_key, e.target.value)}
                                                                placeholder={isSecondaryBreedField ? "Select secondary cat breed" : "Select cat breed"}
                                                                className={errors[field_key] ? 'error' : ''}
                                                            />
                                                            {errors[field_key] && (
                                                                <span className="error-message">{errors[field_key]}</span>
                                                            )}
                                                        </div>
                                                    );
                                                }

                                                if (isBreedField && isDog) {
                                                    const dogBreeds = get_dog_breeds();
                                                    // For secondary breed, add 'None' at the beginning
                                                    const breedList = isSecondaryBreedField ? ['None', ...dogBreeds] : dogBreeds;
                                                    const dogBreedOptions = breedList.map(breed => ({
                                                        value: breed,
                                                        label: breed
                                                    }));

                                                    return (
                                                        <div key={index} className="form-field">
                                                            <label htmlFor={field_key} className="form-label">
                                                                {display_name}
                                                                {field.required && <span className="required-asterisk">*</span>}
                                                            </label>
                                                            <Select
                                                                id={field_key}
                                                                options={dogBreedOptions}
                                                                value={form_data[field_key] || (isSecondaryBreedField ? 'None' : '')}
                                                                onChange={(e) => handle_input_change(field_key, e.target.value)}
                                                                placeholder={isSecondaryBreedField ? "Select secondary dog breed" : "Select dog breed"}
                                                                className={errors[field_key] ? 'error' : ''}
                                                            />
                                                            {errors[field_key] && (
                                                                <span className="error-message">{errors[field_key]}</span>
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                
                                                // Render Input for other field types
                                                return (
                                                    <div key={index} className="form-field">
                                                        <label htmlFor={field_key} className="form-label">
                                                            {display_name}
                                                            {field.required && <span className="required-asterisk">*</span>}
                                                        </label>
                                                        <Input
                                                            id={field_key}
                                                            type={field.type === 'number' ? 'number' : 'text'}
                                                            value={form_data[field_key] || ''}
                                                            onChange={(e) => handle_input_change(field_key, e.target.value)}
                                                            placeholder={field.type === 'date' ? 'Enter in format MM/DD/YYYY' : `Enter ${display_name.toLowerCase()}`}
                                                            className={errors[field_key] ? 'error' : ''}
                                                        />
                                                        {errors[field_key] && (
                                                            <span className="error-message">{errors[field_key]}</span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                    </div>
                                </div>
                            )}

                            {/* No fields message */}
                            {(!incubation.data_fields || incubation.data_fields.length === 0) && (
                                <div className="no-fields-message">
                                    <p>No data fields defined for this incubation.</p>
                                    <p>Please contact an administrator to set up data fields.</p>
                                </div>
                            )}
                        </div>
                        
                        <div className="form-actions">
                            <Button
                                type="button"
                                variant="outline"
                                size="medium"
                                className="action-button"
                                onClick={handle_back_click}
                                disabled={submitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                size="medium"
                                className="action-button"
                                disabled={submitting ||
                                    (!incubation.data_fields || incubation.data_fields.length === 0)}
                            >
                                {submitting ? (
                                    <>
                                        <Spinner size="sm" className="inline-spinner" />
                                        Adding Participant...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-user-plus"></i>
                                        Add Participant
                                    </>
                                )}
                            </Button>
                            {incubation?.surveys?.length > 0 && (
                                <Button
                                    type="button"
                                    variant="primary"
                                    size="medium"
                                    className="action-button"
                                    onClick={() => {
                                        if (!validate_form()) {
                                            showWarning('Please fill in all required fields');
                                            return;
                                        }
                                        setSelectedSurveys([]);
                                        setShowSurveyModal(true);
                                    }}
                                    disabled={submitting ||
                                        (!incubation.data_fields || incubation.data_fields.length === 0)}
                                >
                                    <i className="fas fa-paper-plane"></i>
                                    Add Participant & Send Survey
                                </Button>
                            )}
                        </div>
                    </form>
                </Card>
            </div>

            {/* Survey Selection Modal */}
            {showSurveyModal && (
                <div className="survey-modal-overlay" onClick={() => setShowSurveyModal(false)}>
                    <div className="survey-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="survey-modal-header">
                            <h3>Select Surveys to Send</h3>
                            <button className="survey-modal-close" onClick={() => setShowSurveyModal(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="survey-modal-content">
                            <p className="survey-modal-description">
                                Select the surveys you want to include in the welcome email:
                            </p>
                            <div className="survey-checklist">
                                {incubation.surveys.map((survey, index) => (
                                    <label key={index} className="survey-checkbox-item">
                                        <input
                                            type="checkbox"
                                            checked={selectedSurveys.some(s => s.url === survey.url)}
                                            onChange={() => handleSurveyToggle(survey)}
                                        />
                                        <span className="survey-checkbox-custom">
                                            {selectedSurveys.some(s => s.url === survey.url) && (
                                                <i className="fas fa-check"></i>
                                            )}
                                        </span>
                                        <div className="survey-info">
                                            <span className="survey-name">{survey.name}</span>
                                            <span className="survey-url">{survey.url}</span>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="survey-modal-actions">
                            <Button
                                type="button"
                                variant="outline"
                                size="medium"
                                onClick={() => setShowSurveyModal(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                variant="primary"
                                size="medium"
                                onClick={handleSubmitWithSurveys}
                                disabled={submitting || selectedSurveys.length === 0}
                            >
                                {submitting ? 'Adding...' : `Add & Send ${selectedSurveys.length} Survey${selectedSurveys.length !== 1 ? 's' : ''}`}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AddParticipantsScreen;
